/**
 * Returns a new object with all keys from the source except the ones in the exclusion list.
 */
export function pickExcept<T extends Record<string, unknown>>(source: T, keys: string[]): Partial<T> {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(source)) {
    if (!keys.includes(key)) {
      (result as Record<string, unknown>)[key] = value;
    }
  }
  return result;
}

/**
 * Returns a new object with only the defined (non-null, non-undefined) values from the source.
 * Constrained to `object` (not `Record<string, unknown>`) so a named interface type — which
 * TypeScript does not consider assignable to an index-signature type — can be passed directly,
 * not just inline object literals.
 */
export function keepDefined<T extends object>(source: T): Partial<T> {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined && value !== null) {
      (result as Record<string, unknown>)[key] = value;
    }
  }
  return result;
}

/**
 * Type guard that narrows a value to `T`, excluding `null` and `undefined`.
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * True for a defined scalar, or a non-empty array — the common "should this optional filter
 * field be sent?" predicate for criteria/search-body builders, where an absent value and an
 * empty array both mean "no filter".
 */
export function isNonEmptyValue(value: unknown): boolean {
  return Array.isArray(value) ? value.length > 0 : isDefined(value);
}

/**
 * Type guard that narrows a value to a plain key/value record. Arrays are objects in JS and are
 * deliberately NOT excluded — callers that need to reject arrays must check `Array.isArray` too.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
